import {
	AddressEntity,
	BusinessEntity,
} from 'Models/Entities';
import { Colors, Display } from '../Button/Button';

import BusinessEntityFields from './BusinessEntityFields';
import ButtonAsyncState from '../Button/ButtonAsyncState';
import If from '../If/If';
import React, { useEffect } from 'react';
import alertToast from 'Util/ToastifyUtils';
import { observer } from 'mobx-react';
import { store } from 'Models/Store';
import { useState } from 'react';
import { useEntityChangeTracker } from 'Hooks/useHasChanged';
import { runInAction } from 'mobx';
import { AddressModifiableFields } from 'Models/Entities/AddressEntity';
import { BusinessValidation } from '../BusinessNumberInputs/BusinessValidationModel';


export interface BusinessEntityDetailsProps {
	isPrimary: boolean,
	originalBusinessEntity: BusinessEntity,
	primaryBusinessEntity: BusinessEntity,
	openProductModal?: ('add-ppsr' | 'add-approve' | 'add-intel' | 'none');
}

export const canEditBusinessEntity = (businessEntity: BusinessEntity): boolean => {
	if (!businessEntity.isActive || !store.userPermissions.commonManageOrganisations) {
		return false;
	}
	if (store.userType === 'SUPER_USER') {
		return true;
	}
	if (store.userPermissions.commonManageBusinessEntities === 'ALL') {
		return true;
	}
	if (store.userPermissions.commonManageBusinessEntities === 'WITHIN_ORGANISATION') {
		if (!store.getUser?.organisation?.id) {
			return false;
		}
		return businessEntity.organisationId === store.getUser?.organisation?.id;
	}
	if (store.userPermissions.commonManageBusinessEntities === 'WITHIN_BUSINESS_ENTITIES') {
		if (!store.getUser?.businessEntityIds) {
			return false;
		}
		return store.getUser?.businessEntityIds?.indexOf(businessEntity.id) >= 0;
	}

	// In case of any other state, default to no access
	return false;
};

// The modifiable fields on the Business 
export const BusinessModifiableFields = [
	"name", "phone", "email", "dateOfBirth",

	// business numbers
	"abn", "acn", "trusteeACN",

	"trusteeName",
	"enableDrinksLicence",

	// type of business
	"hire", "supplyGnE", "finance",
] as (keyof BusinessEntity)[];


const BusinessEntityDetails: React.FunctionComponent<BusinessEntityDetailsProps> = observer(props => {
	const {
		isPrimary,
		originalBusinessEntity,
		primaryBusinessEntity,
		openProductModal,
	} = props;


	const [businessEntity] = useState(() => {
		const business = new BusinessEntity(originalBusinessEntity)
		// Assign default record so we have a model to modify properties on
		runInAction(() => business.address ??= new AddressEntity(originalBusinessEntity.address))
		return business;
	});

	const [abnacnDetail, setAbnAcnDetail] = useState<BusinessValidation>(new BusinessValidation());
	const [trusteeDetail, setTrusteeDetail] = useState<BusinessValidation>(new BusinessValidation());

	/**
	 * Method to save the updated user information. Raises an alert toast on success and on error.
	 */
	const onSavePressed = async () => {
		try {
			const relationPath = {
				address: {},
			};

			await businessEntity.saveWithFetchBack(relationPath, {},
				`
				address {
					${AddressEntity.getAttributes().join('\n')}
				}
			`);

			originalBusinessEntity.applicationss = [];
			originalBusinessEntity.spgss = [];
			originalBusinessEntity.assignAttributes(businessEntity);

			alertToast('Business Entity details have been successfully updated', 'success');
		} catch (e) {
			alertToast('Business Entity details could not be saved at this moment. Please refresh and try again.', 'error');
			return;
		}
	};

	const {
		isChanged: businessHasChanged,
		changeSignature: businessChangeSignature
	} = useEntityChangeTracker(BusinessEntity, businessEntity, BusinessModifiableFields);

	const {
		isChanged: addressHasChanged,
		changeSignature: addressChangeSignature
	} = useEntityChangeTracker(AddressEntity, businessEntity.address!, AddressModifiableFields);


	useEffect(() => {
		async function validateFieldsOnChange() {
			const options = {
				abnEntityType: abnacnDetail.abnExtraDetails.typeCode ?? ""
			}
			await businessEntity.validateDetailsFields([], options);
		}
		validateFieldsOnChange();

	}, [businessChangeSignature, addressChangeSignature, abnacnDetail])

	function canSave() {
		const validBusinessEntity = Object.keys(businessEntity.errors).length === 0;
		const validAbnAcnDetails = Object.keys(abnacnDetail.errors).length === 0;
		const validTrusteeDetails = Object.keys(trusteeDetail.errors).length === 0;

		const validEmail = (businessEntity.useOrganisationEmail ? primaryBusinessEntity.errors['email'] : businessEntity.errors['email']) == null;
		return (businessHasChanged || addressHasChanged)
			&& validBusinessEntity
			&& validAbnAcnDetails
			&& validTrusteeDetails
			&& validEmail;
	}

	const canEditBusiness = !canEditBusinessEntity(originalBusinessEntity);

	return (
		<div className="business-entity-details">
			<h3>Business entity details</h3>
			<BusinessEntityFields
				organisationEmail={primaryBusinessEntity.email}
				businessEntity={businessEntity}
				originalBusinessEntity={originalBusinessEntity}
				disabled={canEditBusiness}
				isPrimary={isPrimary}
				setAbnAcnDetail={setAbnAcnDetail}
				setTrusteeDetail={setTrusteeDetail}
				openProductModal={openProductModal}
			/>

			<If condition={!canEditBusiness}>
				<div>
					<ButtonAsyncState
						className="save"
						colors={Colors.Primary}
						display={Display.Solid}
						onPress={onSavePressed}
						readonly={!canSave()}
					>
						Save Changes
					</ButtonAsyncState>
				</div>
			</If>
		</div>
	);
});
export default BusinessEntityDetails;
